export namespace RegexHelper {
  export function isComment(content: string, comment: string): boolean {
    return content?.includes(`${comment}`) ?? false
  }

  export function findContentBetweenComments(
    content: string,
    commentStart: string,
    commentStop: string,
  ): string {
    const isCommentStart = isComment(content, commentStart)
    const isCommentStop = isComment(content, commentStop)
    const isOk = isCommentStart && isCommentStop

    if (!isOk) {
      return
    }

    const contentBetween = content.split(commentStart)[1].split(commentStop)[0]

    return contentBetween
  }

  export function insertContentAfterComment(
    content: string,
    comment: string,
    contentAdded: string,
  ): string {
    let lines = content.split('\n')

    const tokenIndex = findIndex(lines, comment)

    if (tokenIndex < 0) {
      return content
    }

    lines = insertItem(lines, contentAdded, tokenIndex + 1)

    const contentUpdated = lines.join('\n')

    return contentUpdated
  }

  /**
   * Returns first captured group of each regex matches.
   */
  export function findRegexMatches(
    content: string,
    regexExp: RegExp,
  ): string[] {
    const keys = []

    const matches = findRegexes(content, regexExp)

    for (const match of matches) {
      const regexExpCopy = new RegExp(regexExp)
      const key = regexExpCopy.exec(match)[1]
      keys.push(key)
    }

    return keys
  }

  /**
   * Returns all captured group of each regex matches.
   */
  export function findRegexCaptures(
    content: string,
    regexExp: RegExp,
  ): string[][] {
    const captures: string[][] = []

    const matches = findRegexes(content, regexExp)

    for (const match of matches) {
      const regexExpCopy = new RegExp(regexExp)
      const groups = regexExpCopy.exec(match).slice(1)
      captures.push(groups)
    }

    return captures
  }

  export function findRegexes(content: string, regexExp: RegExp): string[] {
    const matches = content.match(regexExp) ?? []

    return matches
  }

  export function findLineIndex(content: string, comment: string): number {
    const lines = content.split('\n')
    return lines.findIndex(item => item.includes(comment))
  }

  export function deleteLineIndex(content: string, index: number): string {
    const lines = content.split('\n')
    lines.splice(index, 1)
    return lines.join('\n')
  }

  export function escapeLineJumps(content: string): string {
    const REGEX_LINE_JUMP_ESCAPED = /\\n/g
    const REGEX_LINE_JUMP = /\n/g

    const UNIQUE_TAG = '[line-jump-to-restore]'

    const contentEscaped = content
      .replace(REGEX_LINE_JUMP_ESCAPED, UNIQUE_TAG)
      .replace(REGEX_LINE_JUMP, '\\n')
      .replace(UNIQUE_TAG, '\\n')

    return contentEscaped
  }

  function findIndex(items: string[], token: string): number {
    return items.findIndex(item => item.includes(token))
  }

  function insertItem(items: string[], item: string, index: number): string[] {
    const itemsUpdated = [...items]

    itemsUpdated.splice(index, 0, item)

    return itemsUpdated
  }

  export function mergeCodeResponse(partOne: string, partTwo: string): string {
    const codeTwo = extractCode(partTwo)

    const codeMerged = `${partOne}${codeTwo}\n\`\`\``

    return codeMerged
  }

  export function hasCode(content: string): boolean {
    const REGEX_HEADER = /```\s*\w*\n([\s\S]*)```/g
    const code = RegexHelper.findRegexMatches(content, REGEX_HEADER)
    return code.length > 0
  }

  export function extractCode(content: string): string {
    const REGEX_HEADER = /```\s*\w*\n([\s\S]*)```/g

    const code =
      RegexHelper.findRegexMatches(content, REGEX_HEADER)[0] ?? content

    const codeEnsured = code.split('```')[0]

    return codeEnsured
  }

  export function isValidUrl(url: string): boolean {
    const urlPattern = new RegExp(
      '^(https?:\\/\\/)?' + // protocol
        '((([a-zA-Z\\d]([a-zA-Z\\d-]*[a-zA-Z\\d])*)\\.?)+[a-zA-Z]{2,}|' + // domain name
        'localhost|' + // OR localhost
        '\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}|' + // OR ipv4
        '\\[?[a-fA-F\\d:]+\\]?)' + // OR ipv6
        '(\\:\\d+)?' + // port
        '(\\/[-a-zA-Z\\d%_.~+]*)*' + // path
        '(\\?[;&a-zA-Z\\d%_.~+=-]*)?' + // query string
        '(\\#[-a-zA-Z\\d_]*)?$', // fragment locator
    )
    return urlPattern.test(url)
  }
}
